/**
 * ConsultazioniStorico (#107f) — cronologia (sola lettura) delle consultazioni
 * di un ordine: per ciascuna, tipo (RT/DIR), chi l'ha aperta e quando, il
 * commento richiesto, e — se chiusa — chi ha risposto, quando e la risposta.
 *
 * I dati arrivano già pronti da RichiestaDetail.consultazioni (tutte le
 * consultazioni dell'ordine, ordinate per richiesta_at DESC dal BE).
 */
import { MessageCircle, ArrowRight } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import type { ConsultazioneItem } from '../../api/richieste';
import Badge from '../ui/Badge';

function formatDateTimeIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function statoBadge(stato: ConsultazioneItem['stato']) {
  if (stato === 'aperta') return <Badge variant="purple">{i18n.t('richieste.storico.stato_aperta')}</Badge>;
  if (stato === 'risposta') return <Badge variant="info">{i18n.t('richieste.storico.stato_risposta')}</Badge>;
  return <Badge variant="neutral">{i18n.t('richieste.storico.stato_chiusa')}</Badge>;
}

export default function ConsultazioniStorico({ consultazioni }: { consultazioni: ConsultazioneItem[] }) {
  const { t } = useTranslation();
  if (consultazioni.length === 0) return null;

  return (
    <div className="bg-white border border-stone-mid rounded-xl overflow-hidden" data-testid="consultazioni-storico">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
        <MessageCircle size={15} className="text-[#7C3AED]" />
        <h3 className="text-sm font-semibold text-ink-dark">{t('richieste.storico.titolo')}</h3>
        <span className="ml-auto text-xs text-ink-soft">{consultazioni.length}</span>
      </div>

      <div className="divide-y divide-stone-mid">
        {consultazioni.map((c) => (
          <div key={c.id} className="px-5 py-3 space-y-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge variant="purple">{t('richieste.storico.consultazione', { tipo: c.tipo })}</Badge>
              {statoBadge(c.stato)}
              {c.stato_pre_consultazione && (
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-soft">
                  <ArrowRight size={11} /> {t('richieste.storico.ritorno_a', { stato: c.stato_pre_consultazione })}
                </span>
              )}
            </div>

            <div className="text-xs text-ink-soft">
              <Trans
                i18nKey="richieste.storico.aperta_da"
                values={{ nome: c.richiesta_da_nome ?? t('richieste.workflow.nd'), data: formatDateTimeIT(c.richiesta_at) }}
                components={{ b: <span className="text-ink-dark font-medium" /> }}
              />
            </div>
            <p className="text-sm text-ink-mid whitespace-pre-wrap">{c.commento_richiesta}</p>

            {c.commento_risposta != null && (
              <div className="mt-1.5 pl-3 border-l-2 border-[#DDD6FE]">
                <div className="text-xs text-ink-soft">
                  <Trans
                    i18nKey="richieste.storico.risposta_da"
                    values={{ nome: c.risposta_da_nome ?? t('richieste.workflow.nd'), data: formatDateTimeIT(c.risposta_at) }}
                    components={{ b: <span className="text-ink-dark font-medium" /> }}
                  />
                </div>
                <p className="text-sm text-ink-mid whitespace-pre-wrap">{c.commento_risposta}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
